import {
  type Block,
  type ID,
  selectBlocksByNode,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'

import type { AppState } from '@/types'
import { startsWith } from '@/utils/arrays'
import { ColumnsV1, ColumnsV2, type DataRow } from './types'

export const prepareContext = (state: AppState, nodeId: string): DataRow => {
  const node = state.manager.project.nodes.find((node) => node.id === nodeId)

  return {
    [ColumnsV2.TITLE]: node?.title || '',
    [ColumnsV2.DESCRIPTION]: node?.notes?.seoDescription || '',
    [ColumnsV2.H1]: node?.notes?.seoH1 || '',
    [ColumnsV2.PATHNAME]: '/',
    [ColumnsV1.BLOCK_TITLES]: getValuableBlockTitles(state, nodeId).map(
      // @TODO review this and fix
      // @ts-expect-error - Property 'title' does not exist on type 'Block'
      (block) => block.title
    ),
    [ColumnsV2.BLOCKS]: getValuableBlockTitles(state, nodeId)
      // @TODO review this and fix
      // @ts-expect-error - Property 'title' and 'wireframes' do not exist on type 'Block'
      .map(({ title, wireframes }) =>
        wireframes?.[0] ? `[${wireframes[0]}] ${title}` : title
      )
      .join(';'),
  }
}

export const getValuableBlockTitles = (
  state: AppState,
  nodeId: string
): Block[] => {
  const blockIds = selectBlocksByNode(nodeId)(state)
  const blocks =
    migrationProjectSelector.getProjectBlockOrSymbolDataByIds(blockIds)(state)

  return blocks.filter(
    // @TODO review this and fix
    (block) => !['', 'Header', 'Footer'].includes(block.title.trim())
  )
}

// The section title input caps at this (SectionRow/Input.tsx): a generated title the user
// could not have typed themselves would be odd to hand them.
const SECTION_TITLE_MAX_LENGTH = 30

/**
 * Names the section AI Generate adds to an open project (OCT-5102) after the prompt that
 * produced it, cut to what the title field allows. The modal never submits an empty theme.
 */
export const sectionTitleFromTheme = (theme: string): string => {
  const title = theme.trim()

  if (title.length <= SECTION_TITLE_MAX_LENGTH) {
    return title
  }

  return `${title.slice(0, SECTION_TITLE_MAX_LENGTH - 1).trimEnd()}…`
}

export const removeQuotes = (str: string): string => str.replaceAll('"', '')

export const parseBlockWireframeAndText = (
  value: string
): { wireframe: string; text: string } | null => {
  const match = value.match(/^\s*(\[(\w+)])\s*(.+)\s*$/)
  return (
    (match && {
      wireframe: match[2],
      text: match[3],
    }) ||
    null
  )
}

export class NodesMap {
  private map = new Map<string, ID>()

  has(path: string): boolean {
    return this.map.has(path)
  }

  add(path: string, id: ID) {
    this.map.set(path, id)
  }

  set(path: string, id: ID) {
    this.map.set(path, id)
  }

  getYoungestAncestor(
    path: string
  ): { youngestAncestor: ID; pathPartsNumber: number } | null {
    const pathnameParts = path.split('/').filter(Boolean)
    let youngestAncestor: ID | undefined

    let pathPartsNumber = -1
    Array.from(this.map.keys()).forEach((key) => {
      const keyParts = key.split('/').filter(Boolean)
      if (
        keyParts.length > pathPartsNumber &&
        startsWith(pathnameParts, keyParts)
      ) {
        pathPartsNumber = keyParts.length
        youngestAncestor = this.map.get(key)!
      }
    })

    return youngestAncestor ? { youngestAncestor, pathPartsNumber } : null
  }
}
